import React from 'react';
import classNames from 'classnames';
import { FieldEditorv2 } from '../field-editor/field-editorv2';
import { FieldEditor } from '../field-editor/field-editor';
import { AmplifyCore, cmxDateTime, Utils } from '@codametrix/ui-common';
import { CMXIcon } from '@codametrix/ui-components';

const { conditionalUtils } = AmplifyCore;

const caseEdit: CMxCommonApp.Capability =
  AmplifyCore.state.capabilities.capabilities.caseEdit;

export type InfoCellProps = {
  cellKey: string;
  label: string;
  value: React.ReactElement | string;
  fieldType?: string;
  fieldEditorInfo?: CMxAPI.FieldEditorInfo;
  capability?: CMxCommonApp.Capability;
  additionalInfo?: { [key: string]: any };
  hasLink?: boolean;
  showProviderInfo?: (providerInfo: CMxCommonApp.ProviderSearchParams) => void;
} & React.HTMLAttributes<HTMLDivElement>;

const BASE_FIELD = {
  showCoderNotesInfo: (coderNotesParams: CMxCommonApp.CoderNotesParams) => {},
  showProviderInfo: (providerInfo: CMxCommonApp.ProviderSearchParams) => {},
  updateValue: (value) => {},
  updateCase: (idAndUpdates: any) => {},
  loadPatientDetailOptions: (dictionaryInfo: any) => {}
};
export const UNEDITABLE_FIELD = {
  ...BASE_FIELD,
  editEnabled: false
};

export const EDITABLE_FIELD = {
  ...BASE_FIELD,
  editEnabled: true
};

const InfoCell: React.FC<InfoCellProps> = ({
  additionalInfo,
  capability,
  cellKey,
  fieldType,
  hasLink,
  label,
  showProviderInfo,
  value,
  className = '',
  fieldEditorInfo = { ...UNEDITABLE_FIELD },
  ...spreadable
}: InfoCellProps) => {
  const props = {
    cellKey,
    label,
    value,
    fieldType,
    fieldEditorInfo,
    className,
    capability,
    hasLink,
    additionalInfo,
    showProviderInfo,
    ...spreadable
  };

  const classes = classNames(
    `${cellKey}-cell`,
    'info-cell',
    'encounter-info',
    className
  );

  const additionalInfoHasNA =
    additionalInfo?.providerName === 'N/A' ||
    additionalInfo?.providerNumber === 'N/A' ||
    additionalInfo?.tenantCode === 'N/A';

  const showLink = hasLink ? value !== 'N/A' && !additionalInfoHasNA : false;

  const { updateValue, updateCase } = fieldEditorInfo;

  const isValidDate = (date: string) => {
    if (fieldType === 'date') {
      return (
        fieldType === 'date' &&
        date &&
        (date === 'N/A' || cmxDateTime.parse(date)?.successfullyParsed)
      );
    } else {
      return false;
    }
  };

  const handleProviderClick = (ev?: React.MouseEvent<HTMLElement>) => {
    ev.preventDefault();
    if (showLink) {
      showProviderInfo({
        providerName: additionalInfo?.providerName,
        providerNumber: additionalInfo?.providerNumber,
        tenantCode: additionalInfo?.tenantCode
      });
    }
  };

  const _handleChange = async (val: any) => {
    if (fieldType === 'dictionary' || isValidDate(val?.toString())) {
      updateValue({ fieldKey: cellKey, newValue: val });

      const caseUpdate = {};
      caseUpdate[cellKey] = val;
      updateCase(caseUpdate);
    }
  };

  let showFieldEditor =
    (fieldType === 'dictionary' || isValidDate(value?.toString())) &&
    cellKey !== 'date_of_service' &&
    //@ts-ignore
    (capability ? capability.on : caseEdit.on.value ?? caseEdit.on);
  const hasAmplifyCaseRunV2Access = Utils.doesPathEndWithV2();

  const displayValue =
    typeof value === 'string' || React.isValidElement(value)
      ? value
      : JSON.stringify(value);

  const infoCellContent = (
    //@ts-ignore
    <>
      <div {...spreadable} className={classes} key={`${cellKey}-info-cell`}>
        <span className="encounter-field field-label">{label}</span>
        {/*CQA-UI: for new flow call  FieldEditorv2*/}
        {showFieldEditor ? (
          hasAmplifyCaseRunV2Access ? (
            <FieldEditorv2
              className="field-data"
              {...props}
              value={value}
              onChange={_handleChange}
              onSubmit={() => {}}
              fieldEditorInfo={fieldEditorInfo}
            />
          ) : (
            <FieldEditor
              className="field-data"
              {...props}
              value={value}
              onChange={_handleChange}
              onSubmit={() => {}}
              fieldEditorInfo={fieldEditorInfo}
            />
          )
        ) : (
          <span
            className={classNames(
              'encounter-detail field-data sensitive-data',
              `${cellKey}-field`,
              { 'cmx-link': showLink }
            )}
            onClick={(ev) => handleProviderClick(ev)}>
            {displayValue}
          </span>
        )}
      </div>
    </>
  );

  return infoCellContent;
};
InfoCell.displayName = 'InfoCell';

export { InfoCell };
